import {arrayToObject, mercator, toTimestamp} from "../../_js/utils";
import DataFetcher from "../../_js/polling_datafetcher";


export default function (state, widget) {
    const m = widget.m;
    const txt = widget.txt;
    const dataStore = {};

    const normalizeApiUrl = widget.normalizeApiUrl;


    const transformForecastType = function (forecastType) {
        forecastType.name = '$type--' + forecastType.id;
        forecastType.plot = forecastType.plot || forecastType.id;
        forecastType.plot_name = '$type--' + forecastType.plot;
        return forecastType;
    };


    const transformData = {

        'len,dir': function (data) {
            return data.map(function (column) {
                return column.map(function (cellData) {
                    if (cellData != null) {
                        const speed = cellData[0];
                        const direction = cellData[1];
                        cellData = {
                            speed,
                            direction,
                            // cap wind arrow lengths at 27m/s with 9 arrows (27 / 9 = 3)
                            speedToken: Math.min(Math.round(speed / 3), 9),
                        };
                    }
                    return cellData;
                });
            });
        },

    };


    const fetchImage = function (url) {
        const deferred = m.deferred();
        const img = new Image();
        img.onload = function () {
            deferred.resolve(url);
        };
        img.onerror = function () {
            deferred.reject(url);
        };
        img.src = url;
        return deferred.promise;
    };

    const fetchTimepointData = function (timepoint, data_meta) {
        const data_url = state.forecastData.data_url
            .replace('[variable]', data_meta.variable)
            .replace('[job_ref]', timepoint.job_ref)
            .replace('[step]', timepoint.step);
        return m.request({
            method: 'GET',
            url: data_url,
            background: true,
            // initialValue: [],
        }).then(function (jsonData) {
            const transformer = transformData[jsonData.shape];
            return transformer ? transformer(jsonData.data) : jsonData.data;
        }).catch(function (err) {
            console.error(err);
        });
    };


    const loadQueue = [];
    loadQueue.maxLength = 25;
    loadQueue.maxActive = 1;
    loadQueue.active = 0;
    loadQueue.loadNext = function () {
        if (loadQueue.length && loadQueue.active <= loadQueue.maxActive) {
            const job = loadQueue.shift();
            const timepoint = job.timepoint;
            const forecastTypeId = job.forecastTypeId;

            const plot_url_tpl = (state.forecastType.static ? state.forecastData.static_plot_url : state.forecastData.plot_url).replace('[plot_spec]', state.forecastType.plot);
            if (state.forecastTypeId === forecastTypeId && !timepoint._loaded[forecastTypeId]) {
                const _loaded = timepoint._loaded[forecastTypeId] = {};
                const timepointResources = [];
                if (!_loaded.plot) {
                    const plot_url = plot_url_tpl
                        .replace('[job_ref]', timepoint.job_ref)
                        .replace('[step]', timepoint.step);
                    timepointResources.push(
                        fetchImage(plot_url)
                            .then(function (plotUrl) {
                                _loaded.plot = plotUrl;
                            })
                            .catch(ex => {
                                console.warn('Problem loading data', ex)
                            })
                    );
                }
                if (state.forecastType.data) {
                    timepointResources.push(
                        fetchTimepointData(timepoint, state.forecastType.data)
                            .then(function (data) {
                                _loaded.data = data;
                            })
                            .catch(() => {
                            })
                    );
                }
                m.sync(timepointResources)
                    .then(function () {
                        timepoint._idObj = {};
                        if (timepoint === state.timepoint) {
                            m.redraw();
                        }
                        loadQueue.active--;
                        loadQueue.loadNext();
                    });
                loadQueue.active++;
            }
            loadQueue.loadNext();
        }
    };

    const loadTimepointData = function (timepoints, lowPriority) {
        timepoints.forEach(function (timepoint) {
            if (timepoint) {
                const forecastTypeId = state.forecastTypeId;
                if (!timepoint._loaded[forecastTypeId]) {
                    loadQueue[lowPriority ? 'push' : 'unshift']({
                        timepoint,
                        forecastTypeId,
                    });
                }
            }
        });
        loadQueue.splice(loadQueue.maxLength); // trim loadQueue to maxLength
        loadQueue.loadNext();
    };


    const transformTimepoint = function (timepoint, idx) {
        timepoint._idObj = {}; // Hack to facilitate quick sameness comparison during render
        timepoint.idx = idx;

        let _loaded;
        timepoint._loaded = _loaded = {};
        timepoint.isLoaded = function () {
            return _loaded[state.forecastTypeId];
        };
        timepoint._plot_url = normalizeApiUrl(timepoint.plot);
        timepoint.plot = function () {
            return (_loaded[state.forecastTypeId] || {}).plot;
        };
        timepoint._data_url = normalizeApiUrl(timepoint.data);
        timepoint.data = function () {
            return (_loaded[state.forecastTypeId] || {}).data;
        };

        // NOTE: timezone should not be part of the .time string
        // instead times should be in the forecast's local timezone
        // and  timezone info should be embedded via the forecast data.
        const time = new Date(timepoint.time);
        time.setUTCSeconds(0);
        time.setUTCMilliseconds(0);
        timepoint.isoDate = timepoint.time;
        timepoint.time = time.getTime();

        return timepoint;
    };


    const transformForecastData = function (forecastData) {
        const _map_url = normalizeApiUrl(forecastData.geo.map_url);
        forecastData.geo.map_url = function () {
            return _map_url;
        };
        forecastData.forecast_typesArr = forecastData.forecast_types.map(transformForecastType);
        forecastData.forecast_types = arrayToObject(forecastData.forecast_typesArr, 'id');

        forecastData.scale_url = normalizeApiUrl(forecastData.scale_url);
        forecastData.data_url = normalizeApiUrl(forecastData.data_url);
        forecastData.plot_url = normalizeApiUrl(forecastData.plot_url);

        const geo = forecastData.geo;
        geo.we_lon = geo.e_lon - geo.w_lon;
        geo.sn_lat = geo.n_lat - geo.s_lat;
        geo.merc_n_lat = mercator(geo.n_lat);
        geo.merc_sn_lat = geo.merc_n_lat - mercator(geo.s_lat);

        const timestamps = forecastData.timestamps = forecastData.timestamps || {};
        // timestamps.analysis = toTimestamp( timestamps.analysis );
        timestamps.first_stale_time = toTimestamp(timestamps.first_stale_time);
        timestamps.expected_end = toTimestamp(timestamps.expected_end);

        forecastData.timeline = forecastData.timeline.map(transformTimepoint);
        return forecastData;
    };


    const getDataGrid = function (forecastData) {
        forecastData.datagrid = undefined;
        if (forecastData.datagrid_url) {
            return m.request({
                method: 'GET',
                url: forecastData.datagrid_url,
                background: true,
            })
                .then(function (datagrid) {
                    // build positions grid from the position_x and position_y grids
                    const block_x = datagrid.position_x || [];
                    const block_y = datagrid.position_y || [];
                    const block_css = datagrid.positions = [];
                    const numCols = block_x.length;
                    for (let c = 0; c < numCols; c++) {
                        const column_x = block_x[c] || [];
                        const column_y = block_y[c] || [];
                        const column_css = block_css[c] = [];
                        const numRows = column_x.length;
                        for (let r = 0; r < numRows; r++) {
                            column_css[r] = column_x[r] != null ?
                                            ('left:' + (column_x[r] / 10) + '%;top:' + (column_y[r] / 10) + '%;') :
                                            undefined;
                        }
                    }
                    delete datagrid.position_x;
                    delete datagrid.position_y;
                    forecastData.datagrid = datagrid;
                    return forecastData;
                });
        } else {
            const dummy = m.deferred();
            setTimeout(function () { // Mithril runs .then()s on resolved promises synchronously
                dummy.resolve(forecastData);
            }, 0);
            return dummy.promise;
        }
    };


    // Gets and refreshes data for the selected forecast
    const getForecastData = DataFetcher({
        label: 'getForecastData',
        req: (args) => ({
            url: args.forecast.url,
        }),
        success: (forecastDataSansDataGrid, args) => {
            return getDataGrid(transformForecastData(forecastDataSansDataGrid))
                .then(forecastData => {
                    state.liftCatastrophe('forecastdata');
                    args.callback(forecastData);
                    m.redraw();
                    return {expires: forecastDataSansDataGrid.expires, redraw: false};
                });
        },
        failure: error => {
            state.flagCatastrophe('forecastdata', 'Error Loading Forecast Data', error);
        },
    });


    // Gets and refreshes list of forecasts
    const getWidgetData = DataFetcher({
        label: 'getWidgetData',
        req: (/*args*/) => ({
            url: widget.dataUrl,
        }),
        success: function (widgetData, args) {
            state.liftCatastrophe('widgetdata');
            txt.addKeys(widgetData.i18n);
            const forecastsById = {};
            const transformForecast = function (forecast) {
                if (forecast.id) {
                    forecastsById[forecast.id] = forecast;
                    forecast.url = normalizeApiUrl(forecast.url);
                    if (widgetData.defaultForecastId == null) {
                        widgetData.defaultForecastId = forecast.id;
                    }
                }
                let nameId = forecast.id;
                if (!nameId) {
                    nameId = forecast.name;
                    if (typeof nameId !== 'string') {
                        nameId = Object.keys(nameId).map((key) => key + '-' + nameId[key]).join('--');
                    }
                }
                const nameKey = '$forecastname--' + nameId;
                txt.addApiDataName(nameKey, forecast.name || forecast.id);
                forecast.rawName = forecast.name;
                forecast.name = nameKey;
                forecast.forecasts = forecast.forecasts && forecast.forecasts.map(transformForecast);
                return forecast;
            };
            widgetData.forecasts = widgetData.forecasts.map(transformForecast);
            widgetData.forecastsById = forecastsById;
            args.callback(widgetData);
            return {expires: widgetData.expires};
        },
        failure: function (error) {
            state.flagCatastrophe('widgetdata', 'Error Loading Trellis Weather Data', error);
        },
    });


    state.on('clear', function () {
        getWidgetData.cancel();
        getForecastData.cancel();
    });


    dataStore.loadTimepointData = loadTimepointData;
    dataStore.fetchImage = fetchImage;
    dataStore.getForecastData = getForecastData;
    dataStore.getWidgetData = getWidgetData;

    return dataStore;

}
